import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Phone, MapPin, Send, ArrowRight } from "lucide-react";

const API = `${window.location.origin}/api`;

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you shortly.");
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-slate-50" data-testid="contact-section">
      <div className="container-main">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <span className="text-sm font-mono uppercase tracking-widest text-blue-600 mb-4 block">
              Contact
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Let's discuss your ITSM needs
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Ready to transform your IT service management? Our team is here to help you 
              get started with KineServe.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <a 
                href="mailto:info@greybulltechnologies.com"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 bg-white border border-slate-200 flex items-center justify-center group-hover:bg-blue-50 group-hover:border-blue-200 transition-colors">
                  <Mail size={20} className="text-slate-600 group-hover:text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Email</div>
                  <div className="text-slate-900 font-medium group-hover:text-blue-600 transition-colors">
                    info@greybulltechnologies.com
                  </div>
                </div>
              </a>

              <a 
                href="tel:+918382072631"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 bg-white border border-slate-200 flex items-center justify-center group-hover:bg-blue-50 group-hover:border-blue-200 transition-colors">
                  <Phone size={20} className="text-slate-600 group-hover:text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Phone</div>
                  <div className="text-slate-900 font-medium group-hover:text-blue-600 transition-colors">
                    +91 838 207 2631
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white border border-slate-200 flex items-center justify-center">
                  <MapPin size={20} className="text-slate-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Powered by</div>
                  <div className="text-slate-900 font-medium">
                    Grey Bull Technologies
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white border border-slate-200 p-8 md:p-10">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6">
              Send us a message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="form-label">Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="John Doe"
                    required
                    className="form-input"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="form-label">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="john@company.com"
                    required
                    className="form-input"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Company</label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Your Company Name"
                  className="form-input"
                  data-testid="contact-company-input"
                />
              </div>

              <div>
                <label className="form-label">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell us about your ITSM requirements..."
                  required
                  rows={4}
                  className="form-input resize-none py-3"
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 py-4 font-medium btn-lift"
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message
                    <Send size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
